import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  useDisclosure,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Avatar,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Card,
  CardBody,
  Collapse,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiEye,
  FiEdit,
  FiPlus,
  FiSearch,
  FiUser,
  FiMail,
  FiPhone,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { digitalFilesService } from "../digitalFilesService";
import PersonalInfoModal from "./PersonalInfoModal";

const PersonalInfoTab = ({
  employees,
  onEmployeeSelect,
  onViewProfile,
  refreshData,
}) => {
  // Dark mode colors
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  // Responsive layout
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [personalInfo, setPersonalInfo] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Fetch personal information for all employees
  const fetchPersonalInfo = async () => {
    try {
      setLoading(true);
      const response =
        await digitalFilesService.personalInfo.getAllPersonalInfo();
      setPersonalInfo(response.data || []);
    } catch (error) {
      console.error("Error fetching personal info:", error);
      toast({
        title: "Error loading personal information",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setPersonalInfo([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonalInfo();
  }, []);

  const handleViewPersonalInfo = async (employee) => {
    try {
      const response = await digitalFilesService.personalInfo.getPersonalInfo(
        employee.employeeId
      );
      setSelectedEmployee({
        ...employee,
        personalInfo: response.data,
      });
      onOpen();
    } catch (error) {
      toast({
        title: "Error loading personal information",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreatePersonalInfo = (employee) => {
    setSelectedEmployee(employee);
    onOpen();
  };

  // Filter employees based on search query
  const filteredEmployees = employees.filter((employee) => {
    const personalInfoData = personalInfo.find(
      (p) => p.employeeId === employee.employeeId
    );
    const searchString = [
      employee.employeeId,
      employee.name,
      employee.email,
      personalInfoData?.personalInfo?.firstName,
      personalInfoData?.personalInfo?.lastName,
    ]
      .join(" ")
      .toLowerCase();

    return searchString.includes(searchQuery.toLowerCase());
  });

  const getPersonalInfoStatus = (employee) => {
    const personalInfoData = personalInfo.find(
      (p) => p.employeeId === employee.employeeId
    );
    return personalInfoData ? "Complete" : "Not Set";
  };

  const getPersonalInfoBadgeColor = (status) => {
    return status === "Complete" ? "green" : "orange";
  };

  const toggleCardExpansion = (employeeId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  // Mobile Card View Component
  const MobileEmployeeCard = ({ employee, personalInfoData, status }) => {
    const isExpanded = expandedCards[employee._id];

    return (
      <Card
        bg={cardBg}
        borderColor={borderColor}
        border="1px"
        mb={3}
        _hover={{ shadow: "md", borderColor: "blue.500" }}
      >
        <CardBody p={4}>
          <VStack align="stretch" spacing={3}>
            {/* Header */}
            <HStack justify="space-between">
              <HStack spacing={3} flex="1">
                <Avatar
                  size="sm"
                  name={employee.name}
                  src={personalInfoData?.personalInfo?.photo}
                />
                <VStack align="start" spacing={0} flex="1">
                  <Text fontWeight="semibold" fontSize="sm" color={textColor}>
                    {employee.name}
                  </Text>
                  <Text fontSize="xs" color={mutedColor}>
                    ID: {employee.employeeId}
                  </Text>
                </VStack>
              </HStack>
              <Badge
                colorScheme={getPersonalInfoBadgeColor(status)}
                fontSize="xs"
              >
                {status}
              </Badge>
            </HStack>

            {/* Emergency Contacts Count */}
            <HStack justify="space-between" fontSize="xs" color={mutedColor}>
              <Text>
                {personalInfoData?.emergencyContacts?.length || 0} Emergency
                Contacts
              </Text>
              <IconButton
                icon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                size="xs"
                variant="ghost"
                onClick={() => toggleCardExpansion(employee._id)}
                aria-label="Toggle details"
              />
            </HStack>

            {/* Collapsible Contact Info */}
            <Collapse in={isExpanded} animateOpacity>
              <VStack
                align="stretch"
                spacing={2}
                pt={2}
                borderTop="1px"
                borderColor={borderColor}
              >
                <HStack spacing={2}>
                  <Icon as={FiMail} boxSize={3} color={mutedColor} />
                  <Text fontSize="xs" color={mutedColor}>
                    Email:
                  </Text>
                  <Text fontSize="xs" flex="1" textAlign="right">
                    {personalInfoData?.personalInfo?.email || employee.email}
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FiPhone} boxSize={3} color={mutedColor} />
                  <Text fontSize="xs" color={mutedColor}>
                    Phone:
                  </Text>
                  <Text fontSize="xs" flex="1" textAlign="right">
                    {personalInfoData?.personalInfo?.phone || employee.phone}
                  </Text>
                </HStack>
              </VStack>
            </Collapse>

            {/* Actions */}
            <HStack justify="flex-end" spacing={2} pt={2}>
              <Tooltip label="View">
                <IconButton
                  icon={<FiEye />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleViewPersonalInfo(employee)}
                  aria-label="View personal info"
                  minW="44px"
                  minH="44px"
                />
              </Tooltip>
              <Tooltip label="Edit">
                <IconButton
                  icon={<FiEdit />}
                  size="sm"
                  colorScheme="blue"
                  variant="ghost"
                  onClick={() => handleCreatePersonalInfo(employee)}
                  aria-label="Edit personal info"
                  minW="44px"
                  minH="44px"
                />
              </Tooltip>
              <Tooltip label="Profile">
                <IconButton
                  icon={<FiUser />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onViewProfile(employee)}
                  aria-label="View profile"
                  minW="44px"
                  minH="44px"
                />
              </Tooltip>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box position="relative">
      {/* Header - Hidden on mobile */}
      <Box display={{ base: "none", md: "block" }} mb={6}>
        <Flex justify="space-between" align="center">
          <Box>
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Personal Information Management
            </Text>
            <Text color={mutedColor}>
              Manage comprehensive personal details for all employees
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={() => {
              setSelectedEmployee(null);
              onOpen();
            }}
          >
            Add Personal Info
          </Button>
        </Flex>
      </Box>

      {/* Search - Full width on mobile */}
      <InputGroup mb={{ base: 4, md: 6 }} maxW={{ base: "100%", md: "400px" }}>
        <InputLeftElement pointerEvents="none">
          <FiSearch color="gray" />
        </InputLeftElement>
        <Input
          placeholder="Search employees..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          minH="44px"
        />
      </InputGroup>

      {/* Mobile Card View */}
      {isMobile ? (
        <VStack spacing={3} align="stretch" pb={20}>
          {filteredEmployees.map((employee) => {
            const personalInfoData = personalInfo.find(
              (p) => p.employeeId === employee.employeeId
            );
            const status = getPersonalInfoStatus(employee);

            return (
              <MobileEmployeeCard
                key={employee._id}
                employee={employee}
                personalInfoData={personalInfoData}
                status={status}
              />
            );
          })}
        </VStack>
      ) : (
        <Box
          overflowX="auto"
          borderRadius="lg"
          border="1px"
          borderColor={borderColor}
        >
          <Table variant="simple">
            <Thead bg={useColorModeValue("gray.50", "gray.700")}>
              <Tr>
                <Th>Employee</Th>
                <Th>Personal Info Status</Th>
                <Th>Contact Info</Th>
                <Th>Emergency Contacts</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredEmployees.map((employee) => {
                const personalInfoData = personalInfo.find(
                  (p) => p.employeeId === employee.employeeId
                );
                const status = getPersonalInfoStatus(employee);

                return (
                  <Tr key={employee._id} _hover={{ bg: hoverBg }}>
                    <Td>
                      <HStack spacing={3}>
                        <Avatar
                          size="sm"
                          name={employee.name}
                          src={personalInfoData?.personalInfo?.photo}
                        />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">{employee.name}</Text>
                          <Text fontSize="sm" color={mutedColor}>
                            ID: {employee.employeeId}
                          </Text>
                        </VStack>
                      </HStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={getPersonalInfoBadgeColor(status)}>
                        {status}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <HStack>
                          <FiMail size={14} />
                          <Text fontSize="sm">
                            {personalInfoData?.personalInfo?.email ||
                              employee.email}
                          </Text>
                        </HStack>
                        <HStack>
                          <FiPhone size={14} />
                          <Text fontSize="sm">
                            {personalInfoData?.personalInfo?.phone ||
                              employee.phone}
                          </Text>
                        </HStack>
                      </VStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {personalInfoData?.emergencyContacts?.length || 0}{" "}
                        contacts
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewPersonalInfo(employee)}
                          aria-label="View personal info"
                        />
                        <IconButton
                          icon={<FiEdit />}
                          size="sm"
                          colorScheme="blue"
                          onClick={() => handleCreatePersonalInfo(employee)}
                          aria-label="Edit personal info"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onViewProfile(employee)}
                        >
                          Profile
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Floating Action Button - Mobile Only */}
      {isMobile && (
        <IconButton
          icon={<FiPlus />}
          colorScheme="blue"
          size="lg"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          boxShadow="lg"
          onClick={() => {
            setSelectedEmployee(null);
            onOpen();
          }}
          aria-label="Add Personal Info"
          w="56px"
          h="56px"
          zIndex={10}
        />
      )}

      {/* Personal Info Modal */}
      <PersonalInfoModal
        isOpen={isOpen}
        onClose={onClose}
        employee={selectedEmployee}
        onSuccess={() => {
          fetchPersonalInfo();
          refreshData();
        }}
      />
    </Box>
  );
};

export default PersonalInfoTab;
